import { ChangeEvent, MouseEventHandler, useRef, useState } from "react";
import toast from "react-hot-toast";
import _capitalize from "lodash/capitalize";

import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { clsxMerge } from "shared/lib/helpers";
import { readFileAsDataURL } from "shared/lib/helpers/file";
import Avatar from "shared/ui/avatar";
import { LoadingSpinner } from "shared/ui/spinners/loading-spinner";
import { Dropzone, DropzoneImperativeRefI } from "shared/ui/uploader/dropzone";
import { FILE_SIZE_BYTES } from "shared/lib/constants/files";

import { validateSelectedAvatar } from "./utils";
import {
  getPictureUploadValidationMessages,
  DROPZONE_ACCEPT,
  MAX_AVATAR_SIZE_MB,
  SUPPORTED_AVATAR_FORMATS_LIST,
  SUPPORTED_AVATAR_FORMATS_READABLE_LIST,
} from "./constants";

interface ProfileImageSelectorPropsI extends PropsWithClassNameI {
  savedPictureURL?: string;
  isReadonly?: boolean;
  fallbackText?: string;
  isUploadingPicture: boolean;
  onChangePicture: (file: File | undefined) => void;
  pictureLabel?: string;
  maxFileSizeMb?: number;
}

type InputChangeEventI = ChangeEvent<HTMLInputElement>;

const PICTURE_BLOCK_CLASSNAME = "h-[166px] w-[166px] rounded-full";

export const PictureSelector = ({
  className,
  isReadonly = false,
  savedPictureURL,
  isUploadingPicture,
  onChangePicture,
  fallbackText = "You",
  pictureLabel = "profile picture",
  maxFileSizeMb = MAX_AVATAR_SIZE_MB,
}: ProfileImageSelectorPropsI) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewDataURL, setPreviewDataURL] = useState<string>();
  const dropzoneImperativeRef = useRef<DropzoneImperativeRefI>(null);

  const avatarImageURL = previewDataURL || savedPictureURL;

  const handleChange = async (e: InputChangeEventI) => {
    if (isReadonly) {
      return;
    }

    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const { isValid, conditions } = validateSelectedAvatar(file, maxFileSizeMb);

    const validationMessages =
      getPictureUploadValidationMessages(maxFileSizeMb);

    if (!isValid) {
      if (!conditions.format && !conditions.size) {
        toast.error(validationMessages.BOTH);
      } else {
        toast.error(
          !conditions.format
            ? validationMessages.FORMAT
            : validationMessages.SIZE
        );
      }

      return;
    }

    onChangePicture(file);

    const imageDataURL = await readFileAsDataURL(file);

    if (imageDataURL) {
      setPreviewDataURL(imageDataURL);
    }
  };

  const handleDropFile = (files: File[]) => {
    /**
     * A little hack to get access to a real FileList object with files as FileList
     * is not directly constructable
     */
    const dataTransfer = new DataTransfer();
    files.forEach((file) => dataTransfer.items.add(file));

    return handleChange({
      target: {
        files: dataTransfer.files,
      },
    } as InputChangeEventI);
  };

  const handleCancelSelectedImage: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setPreviewDataURL(undefined);
    onChangePicture(undefined);
  };

  return (
    <section className={clsxMerge("flex w-full flex-col gap-2.5", className)}>
      <div className="typography-body-4-medium flex w-full justify-between">
        <span>
          {isReadonly ? _capitalize(pictureLabel) : `Upload a ${pictureLabel}`}
        </span>
        {!isReadonly && <span className="text-[#999]">Optional</span>}
      </div>

      {avatarImageURL ? (
        <div
          className={clsxMerge(
            "flex w-full flex-col items-center pt-8",
            "rounded-md border-[1px] border-[#ccc] pb-6",
            isUploadingPicture || isReadonly
              ? "cursor-not-allowed"
              : "hover:bg-black/3 cursor-pointer transition-colors"
          )}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            disabled={isReadonly}
            ref={fileInputRef}
            type="file"
            multiple={false}
            className="hidden"
            accept={SUPPORTED_AVATAR_FORMATS_LIST}
            onChange={handleChange}
          />

          {avatarImageURL ? (
            <div
              className={clsxMerge(
                "relative transition-colors",
                PICTURE_BLOCK_CLASSNAME,
                isUploadingPicture || (isReadonly && "bg-[rgb(0,0,0,.2)]")
              )}
            >
              <Avatar
                isLazy
                src={avatarImageURL}
                placeholderText={fallbackText}
                className={clsxMerge(
                  PICTURE_BLOCK_CLASSNAME,
                  "animate-fadein typography-body-2",
                  isUploadingPicture && "opacity-50"
                )}
              />

              {isUploadingPicture && (
                <div className="absolute-centered-xy">
                  <LoadingSpinner className="text-white" />
                </div>
              )}
            </div>
          ) : (
            <div
              className={clsxMerge(
                PICTURE_BLOCK_CLASSNAME,
                "flex items-center justify-center",
                "typography-body-2 bg-black  text-white"
              )}
            >
              {fallbackText}
            </div>
          )}

          {!isReadonly && (
            <div className="typography-body-4 flex gap-1 pt-4 text-[#4474E3]">
              <span>Choose a different image</span>

              {previewDataURL && (
                <>
                  <span>|</span>
                  <span onClick={handleCancelSelectedImage}>
                    Cancel selected image
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        <Dropzone
          ref={dropzoneImperativeRef}
          onDrop={handleDropFile}
          maxSize={maxFileSizeMb * FILE_SIZE_BYTES.ONE_MEGABYTE}
          multiple={false}
          accept={DROPZONE_ACCEPT}
        />
      )}

      {!isReadonly && (
        <div className="typography-body-5 flex w-full justify-between text-[#999]">
          <span>Supports {SUPPORTED_AVATAR_FORMATS_READABLE_LIST}</span>
          <span>Maximum size: {maxFileSizeMb}MB</span>
        </div>
      )}
    </section>
  );
};
